import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import api from '../services/api'
import toast from 'react-hot-toast'

const PERMISOS_LISTA = [
  { key: 'ventas',        label: 'Punto de venta',  desc: 'Realizar ventas, devoluciones, reimprimir boletas' },
  { key: 'caja',          label: 'Caja',             desc: 'Abrir/cerrar caja, movimientos de dinero' },
  { key: 'inventario',    label: 'Inventario',       desc: 'Productos, stock, precios, recepción' },
  { key: 'clientes',      label: 'Clientes',         desc: 'Ver y crear clientes' },
  { key: 'reportes',      label: 'Reportes',         desc: 'Ver ventas, estadísticas y análisis' },
  { key: 'administrador', label: 'Administrador',    desc: 'Usuarios, configuración y acceso total' },
]

const PERMISOS_DEFAULT = { ventas: false, caja: false, inventario: false, clientes: false, reportes: false, administrador: false }

const modalVacio = { nombre: '', email: '', password: '', permisos: { ...PERMISOS_DEFAULT }, activo: true }

function getRolLabel(permisos) {
  if (!permisos) return '—'
  if (permisos.administrador) return 'Administrador'
  const activos = PERMISOS_LISTA.filter(p => p.key !== 'administrador' && permisos[p.key]).map(p => p.label)
  return activos.length > 0 ? activos.join(', ') : 'Sin permisos'
}

export default function Usuarios() {
  const { usuario } = useAuth()
  const [usuarios, setUsuarios] = useState([])
  const [cargando, setCargando] = useState(true)
  const [modal, setModal] = useState(false)
  const [editando, setEditando] = useState(null)
  const [form, setForm] = useState(modalVacio)
  const [guardando, setGuardando] = useState(false)
  const [confirmarEliminar, setConfirmarEliminar] = useState(null)

  useEffect(() => { cargar() }, [])

  const cargar = async () => {
    setCargando(true)
    try {
      const { data } = await api.get('/usuarios')
      setUsuarios(data)
    } catch { toast.error('Error al cargar usuarios') }
    finally { setCargando(false) }
  }

  const abrirCrear = () => {
    setEditando(null)
    setForm(modalVacio)
    setModal(true)
  }

  const abrirEditar = (u) => {
    setEditando(u)
    setForm({
      nombre: u.nombre,
      email: u.email,
      password: '',
      permisos: { ...PERMISOS_DEFAULT, ...(u.permisos || {}) },
      activo: u.activo
    })
    setModal(true)
  }

  const cerrarModal = () => { setModal(false); setEditando(null); setForm(modalVacio) }

  const togglePermiso = (key) => {
    setForm(prev => ({
      ...prev,
      permisos: { ...prev.permisos, [key]: !prev.permisos[key] }
    }))
  }

  const guardar = async () => {
    if (!form.nombre.trim() || !form.email.trim()) { toast.error('Nombre y email son requeridos'); return }
    if (!editando && !form.password) { toast.error('La contraseña es requerida'); return }
    const sinPermisos = Object.values(form.permisos).every(v => !v)
    if (sinPermisos) { toast.error('Selecciona al menos un permiso'); return }

    setGuardando(true)
    try {
      if (editando) {
        const payload = { nombre: form.nombre, permisos: form.permisos, activo: form.activo }
        if (form.password) payload.password = form.password
        await api.put(`/usuarios/${editando.id}`, payload)
        toast.success('Usuario actualizado')
      } else {
        await api.post('/usuarios', { nombre: form.nombre, email: form.email, password: form.password, permisos: form.permisos })
        toast.success('Usuario creado correctamente')
      }
      cerrarModal()
      cargar()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar usuario')
    } finally { setGuardando(false) }
  }

  const desactivar = async (u) => {
    try {
      await api.delete(`/usuarios/${u.id}`)
      toast.success(`${u.nombre} desactivado`)
      setConfirmarEliminar(null)
      cargar()
    } catch (err) { toast.error(err.response?.data?.error || 'Error al desactivar') }
  }

  const reactivar = async (u) => {
    try {
      await api.put(`/usuarios/${u.id}`, { activo: true })
      toast.success(`${u.nombre} reactivado`)
      cargar()
    } catch { toast.error('Error al reactivar usuario') }
  }

  if (cargando) return <div className="page-loading"><div className="spinner"/></div>

  return (
    <div>
      <div className="flex-between mb-6">
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.25rem' }}>Usuarios</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            {usuarios.length} usuario{usuarios.length !== 1 ? 's' : ''} en tu empresa
          </p>
        </div>
        <button className="btn btn-primary" onClick={abrirCrear}>+ Nuevo usuario</button>
      </div>

      {usuarios.length === 0 ? (
        <div className="empty-state">
          <p>No hay usuarios creados aún.</p>
          <button className="btn btn-primary" onClick={abrirCrear}>Crear primer usuario</button>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Permisos</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.map(u => (
                  <tr key={u.id} style={{ opacity: u.activo ? 1 : 0.5 }}>
                    <td style={{ fontWeight: 500 }}>{u.nombre}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{u.email}</td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{getRolLabel(u.permisos)}</td>
                    <td>
                      <span className={u.activo ? 'badge badge-success' : 'badge badge-danger'}>
                        {u.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button className="btn btn-secondary" style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }} onClick={() => abrirEditar(u)}>Editar</button>
                        {u.id !== usuario?.id && (
                          u.activo
                            ? <button className="btn btn-danger" style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }} onClick={() => setConfirmarEliminar(u)}>Desactivar</button>
                            : <button className="btn btn-secondary" style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }} onClick={() => reactivar(u)}>Reactivar</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODAL CREAR / EDITAR */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: '100%', maxWidth: '520px', margin: '1rem', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontWeight: 700, marginBottom: '1.5rem', fontSize: '1.1rem' }}>
              {editando ? 'Editar usuario' : 'Nuevo usuario'}
            </h2>

            <div className="form-group">
              <label className="form-label">Nombre completo *</label>
              <input className="form-input" value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Juan Pérez" autoFocus/>
            </div>

            <div className="form-group">
              <label className="form-label">Email *</label>
              <input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="correo@ejemplo.com" disabled={!!editando}
                style={editando ? { background: 'var(--bg-secondary)', color: 'var(--text-secondary)' } : {}}/>
              {editando && <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>El email no se puede modificar</p>}
            </div>

            <div className="form-group">
              <label className="form-label">{editando ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}</label>
              <input className="form-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder={editando ? 'Dejar vacío para mantener actual' : 'Mínimo 6 caracteres'}/>
            </div>

            {/* PERMISOS */}
            <div className="form-group">
              <label className="form-label">Atribuciones *</label>
              <div style={{ border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
                {PERMISOS_LISTA.map((p, i) => (
                  <div key={p.key} onClick={() => togglePermiso(p.key)} style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '0.75rem 1rem', cursor: 'pointer',
                    background: form.permisos[p.key] ? 'var(--color-primary-light, #eff6ff)' : 'transparent',
                    borderTop: i > 0 ? '1px solid var(--border)' : 'none',
                    transition: 'background 0.15s'
                  }}>
                    <div style={{
                      width: '20px', height: '20px', borderRadius: '4px', flexShrink: 0,
                      border: form.permisos[p.key] ? '2px solid var(--color-primary, #2563eb)' : '2px solid var(--border)',
                      background: form.permisos[p.key] ? 'var(--color-primary, #2563eb)' : 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      {form.permisos[p.key] && <svg width="12" height="12" viewBox="0 0 12 12"><polyline points="2,6 5,9 10,3" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{p.label}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{p.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {editando && (
              <div className="form-group">
                <label className="form-label">Estado</label>
                <select className="form-select" value={form.activo ? 'true' : 'false'} onChange={e => setForm({ ...form, activo: e.target.value === 'true' })}>
                  <option value="true">Activo</option>
                  <option value="false">Inactivo</option>
                </select>
              </div>
            )}

            <div className="flex-between" style={{ marginTop: '1.5rem', gap: '1rem' }}>
              <button className="btn btn-secondary btn-block" onClick={cerrarModal} disabled={guardando}>Cancelar</button>
              <button className="btn btn-primary btn-block" onClick={guardar} disabled={guardando}>
                {guardando ? 'Guardando...' : editando ? 'Guardar cambios' : 'Crear usuario'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CONFIRMAR DESACTIVAR */}
      {confirmarEliminar && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: '100%', maxWidth: '380px', margin: '1rem', textAlign: 'center' }}>
            <h3 style={{ fontWeight: 700, marginBottom: '0.5rem' }}>¿Desactivar usuario?</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
              <strong>{confirmarEliminar.nombre}</strong> no podrá ingresar al sistema. Podrás reactivarlo después.
            </p>
            <div className="flex-between gap-2">
              <button className="btn btn-secondary btn-block" onClick={() => setConfirmarEliminar(null)}>Cancelar</button>
              <button className="btn btn-danger btn-block" onClick={() => desactivar(confirmarEliminar)}>Sí, desactivar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
